import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { DetailCard } from "../../Components/Needed";
import { FaHistory } from "react-icons/fa";
import { triesLabel, showReset, showResetAll, resetBody } from "./kycTries";

// KYC Rejections (Phase 5.7, changed for KYC v2): one card on the customer page with, per document, the status,
// when it is valid until, the rejection reason code, the name-mismatch flag and any override, then the attempt
// timeline (every submission, by whom, the result and why). A rejected document can be overridden: "Send back to
// review" (it moves to Pending, then any reviewer verifies it - an override never approves) or "Keep rejected",
// always with a reason. A proof file is optional. A "no car class" rejection can only be overridden by an admin.
// The admin and staff panels use this same file; they differ in `basePath`, `panel` and `canOverride`.

const DOCS = [
    { key: "aadhaar", label: "Aadhaar", field: "aadhaarNo" },
    { key: "dl", label: "Driving licence", field: "dlNo" },
    { key: "voter", label: "Voter ID", field: "voterId" },
    { key: "passport", label: "Passport", field: "passportNo" },
];
const CODE_LABEL = {
    provider_invalid: "Provider could not verify",
    provider_error: "Provider busy (not counted)",
    duplicate: "Already on another account",
    under_age: "Under 21",
    dob_unreadable: "Date of birth unreadable",
    no_car_class: "No car class (LMV)",
    expiring: "Expires within 6 months",
    expired: "Expired",
    legacy: "Rejected (before reason codes)",
};
const RESULT_CLASS = { verified: "success", pending: "warning", rejected: "danger", reopened: "warning" };
// A document is valid until its validUntil date (one year from approval); inside the last 30 days it is "expiring".
const EXPIRING_DAYS = 30;
const validityOf = (doc) => {
    if (!doc?.validUntil) return null;
    const until = new Date(doc.validUntil);
    const left = Math.ceil((until.getTime() - Date.now()) / 86400000);
    if (doc.status === "expired" || left < 0) return { state: "expired", until, left };
    return { state: left <= EXPIRING_DAYS ? "expiring" : "valid", until, left };
};
const VALIDITY_CLASS = { valid: "success", expiring: "warning text-dark", expired: "danger" };
const dateOnly = (value) => new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const EVENT_LABEL_OVERRIDE = { reopened: "sent back to review", rejected: "kept rejected" };
const STATUS_CLASS = { verified: "success", pending: "warning", rejected: "danger", expired: "danger", not_submitted: "secondary" };
const SOURCE_LABEL = { customer: "Customer", admin: "Admin", staff: "Staff" };
// Only a document with its data on this account can be sent back to review (not a duplicate or a failed check).
const canApprove = (doc, field) => !!doc?.[field] && !["duplicate", "provider_invalid", "provider_error"].includes(doc?.rejectionCode);

const OverrideForm = ({ doc, docKey, label, panel, basePath, customerId, onDone, onCancel }) => {
    const [decision, setDecision] = useState("reopen");
    const [reason, setReason] = useState("");
    const [proof, setProof] = useState(null);
    const [busy, setBusy] = useState(false);
    const approveAllowed = canApprove(doc, DOCS.find((d) => d.key === docKey)?.field);

    const submit = async () => {
        const text = reason.trim();
        if (text.length < 5 || text.length > 300) { toast.error("Give a reason (5 to 300 characters)"); return; }
        const form = new FormData();
        form.append("decision", decision);
        form.append("reason", text);
        if (proof && panel === "admin") form.append("proof", proof);
        setBusy(true);
        const response = await Handler({ method: "patch", url: `${basePath}/${customerId}/kyc/${docKey}/override`, data: form, formdata: true });
        setBusy(false);
        if (response?.success) {
            toast.success(decision === "reopen" ? `${label} sent back to review` : `${label} kept rejected`);
            onDone?.();
        } else {
            toast.error(response?.data?.reason || response?.data?.message || "Could not save the override");
        }
    };

    return (
        <div className="border rounded-3 p-3 mt-2 bg-light">
            <div className="d-flex gap-3 mb-2 small">
                <label className="d-flex align-items-center gap-1">
                    <input type="radio" checked={decision === "reopen"} onChange={() => setDecision("reopen")} disabled={busy || !approveAllowed} /> Send back to review
                </label>
                <label className="d-flex align-items-center gap-1">
                    <input type="radio" checked={decision === "keep"} onChange={() => setDecision("keep")} disabled={busy} /> Keep rejected
                </label>
            </div>
            {!approveAllowed && <div className="small text-muted mb-2">There is no {label} data on this account to review. The customer needs to submit it again.</div>}
            <div className="small text-muted mb-2">The document goes back to Pending. A reviewer still has to verify it; this does not approve it.</div>
            {doc?.rejectionReason || doc?.rejectionCode ? <div className="small mb-2"><strong>Rejected because:</strong> {CODE_LABEL[doc.rejectionCode] || ""}{doc.rejectionReason ? ` - ${doc.rejectionReason}` : ""}</div> : null}
            <textarea className="form-control form-control-sm mb-2" rows={2} maxLength={300} placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
            {panel === "admin" && (
                <div className="mb-2">
                    <label className="form-label small mb-1">Proof (optional; for example a birth certificate or another government ID; image or PDF)</label>
                    <input type="file" className="form-control form-control-sm" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} disabled={busy} />
                </div>
            )}
            <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving..." : "Save"}</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
            </div>
        </div>
    );
};

const KycReviewPanel = ({ customerId, customer, basePath = "/admin/customer", panel = "admin", canOverride = true, canResetTries = false, onChanged }) => {
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null); // the document whose override form is open
    const [filter, setFilter] = useState("");
    const [today, setToday] = useState({}); // the customer's tries today per document (the daily limit is customer side only)
    const [confirmReset, setConfirmReset] = useState(null); // a document key, or "all"
    const [resetting, setResetting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: `${basePath}/${customerId}/kyc/attempts`, params: filter ? { documentType: filter } : {} });
        setAttempts(response?.success ? response?.data?.data?.attempts || [] : []);
        setToday(response?.success ? response?.data?.data?.today || {} : {});
        setLoading(false);
    }, [basePath, customerId, filter]);

    useEffect(() => { load(); }, [load]);

    const done = () => { setOpen(null); load(); onChanged?.(); };

    // Reset the customer's tries for today (one document, or all). Written to the KYC log with who did it.
    const resetTries = async (documentType) => {
        setResetting(true);
        const response = await Handler({ method: "PATCH", url: `${basePath}/${customerId}/kyc/attempts/reset`, data: resetBody(documentType) });
        setResetting(false);
        setConfirmReset(null);
        if (response?.success) {
            toast.success(response?.data?.message || "Tries reset for today");
            load();
            onChanged?.();
        } else {
            toast.error(response?.data?.message || "Could not reset the tries");
        }
    };

    const ResetButton = ({ docKey, label }) => (confirmReset === docKey ? (
        <span className="d-inline-flex gap-1">
            <button type="button" className="btn btn-sm btn-danger" onClick={() => resetTries(docKey === "all" ? null : docKey)} disabled={resetting}>{resetting ? "Resetting..." : "Confirm reset"}</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmReset(null)} disabled={resetting}>Cancel</button>
        </span>
    ) : (
        <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => setConfirmReset(docKey)}>{label}</button>
    ));

    return (
        <DetailCard title="KYC checks and attempts" icon={<FaHistory />}>
            <div className="table-responsive mb-3">
                <table className="table table-sm align-middle mb-0">
                    <thead><tr><th>Document</th><th>Status</th><th>Valid until</th><th>Reason</th><th>Notes</th><th>Customer tries today</th><th /></tr></thead>
                    <tbody>
                        {DOCS.map((d) => {
                            const doc = customer?.kyc?.[d.key] || {};
                            const status = doc.status || "not_submitted";
                            const adminOnly = doc.rejectionCode === "no_car_class" && panel !== "admin";
                            const showOverride = status === "rejected" && canOverride && !adminOnly;
                            const validity = status === "verified" || status === "expired" ? validityOf(doc) : null;
                            return (
                                <React.Fragment key={d.key}>
                                    <tr>
                                        <td className="fw-semibold">{d.label}</td>
                                        <td><span className={`badge bg-${STATUS_CLASS[status] || "secondary"} text-capitalize`}>{status.replace("_", " ")}</span></td>
                                        <td className="small">
                                            {validity ? (
                                                <>
                                                    <span className={`badge bg-${VALIDITY_CLASS[validity.state]} text-capitalize me-1`}>{validity.state}</span>
                                                    {dateOnly(validity.until)}
                                                    {validity.state === "expiring" ? <span className="text-muted"> ({validity.left} day{validity.left === 1 ? "" : "s"} left)</span> : null}
                                                    {doc.approvedAt ? <div className="text-muted">approved {dateOnly(doc.approvedAt)}</div> : null}
                                                </>
                                            ) : "-"}
                                        </td>
                                        <td className="small">{status === "rejected" ? (CODE_LABEL[doc.rejectionCode] || doc.rejectionReason || "-") : "-"}</td>
                                        <td className="small">
                                            {doc.nameMismatch ? <span className="badge bg-warning text-dark me-1">Name differs from verified documents</span> : null}
                                            {status === "pending" && doc.override?.result === "reopened" ? <div><span className="badge bg-warning text-dark me-1">Reopened</span> Sent back to review by {doc.override.role} ({doc.override.by}): {doc.override.reason}. Any reviewer can verify it.</div> : null}
                                            {doc.override && !(status === "pending" && doc.override.result === "reopened") ? <span className="text-muted">Override by {doc.override.role} {formatDateTime(doc.override.at)} ({EVENT_LABEL_OVERRIDE[doc.override.result] || doc.override.result}): {doc.override.reason}{doc.override.proofUrl ? <> (<a href={doc.override.proofUrl} target="_blank" rel="noopener noreferrer">proof</a>)</> : null}</span> : null}
                                            {!doc.nameMismatch && !doc.override ? "-" : null}
                                        </td>
                                        <td className="small">
                                            {triesLabel(today[d.key])}
                                            {showReset(today[d.key], canResetTries) ? <div className="mt-1"><ResetButton docKey={d.key} label="Reset tries" /></div> : null}
                                        </td>
                                        <td className="text-end">
                                            {showOverride && open !== d.key ? <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setOpen(d.key)}>Override</button> : null}
                                            {status === "rejected" && adminOnly ? <span className="small text-muted">Admin only</span> : null}
                                            {status === "rejected" && !canOverride && !adminOnly ? <span className="small text-muted">Needs override permission</span> : null}
                                        </td>
                                    </tr>
                                    {open === d.key && (
                                        <tr><td colSpan={7}>
                                            <OverrideForm doc={doc} docKey={d.key} label={d.label} panel={panel} basePath={basePath} customerId={customerId} onDone={done} onCancel={() => setOpen(null)} />
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="small text-muted mb-2">The daily tries limit applies to the customer's own submissions only. Tries made here by {panel === "admin" ? "an admin" : "staff"} are never limited and do not use up the customer's tries.</div>
            {showResetAll(today, canResetTries) ? <div className="mb-3"><ResetButton docKey="all" label="Reset all tries for today" /></div> : null}
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">Attempts</h6>
                <select className="form-select form-select-sm w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter attempts by document">
                    <option value="">All documents</option>
                    {DOCS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
            </div>
            {loading ? <div className="small text-muted">Loading...</div> : attempts.length === 0 ? (
                <div className="small text-muted">No KYC attempts recorded yet.</div>
            ) : (
                <ul className="list-unstyled mb-0">
                    {attempts.map((a) => (
                        <li key={a.attemptId} className="border-bottom py-2 small">
                            <div className="d-flex justify-content-between gap-2 flex-wrap">
                                <span>
                                    <span className={`badge bg-${RESULT_CLASS[a.result] || "secondary"} text-capitalize me-2`}>{a.result}</span>
                                    <strong>{DOCS.find((d) => d.key === a.documentType)?.label || a.documentType}</strong>
                                    {a.input?.documentNumber ? <span className="text-muted"> · {a.input.documentNumber}</span> : null}
                                </span>
                                <span className="text-muted">{formatDateTime(a.createdAt)} · {SOURCE_LABEL[a.source] || a.source}{a.counted === false ? " · not counted" : a.source !== "customer" ? " · no daily limit" : a.resetAt ? " · tries reset" : ""}</span>
                            </div>
                            {a.reasonCode ? <div className="text-danger">{CODE_LABEL[a.reasonCode] || a.reasonCode}: {a.reasonText}</div> : null}
                            {a.flags?.includes("name_mismatch") ? <div className="text-warning">Name differs from verified documents{a.input?.name ? ` (${a.input.name})` : ""}</div> : null}
                            {a.override?.at ? <div className="text-muted">Override ({a.override.role}, {formatDateTime(a.override.at)}): {a.override.result === "rejected" ? "kept rejected" : "sent back to review"} - {a.override.reason}</div> : null}
                        </li>
                    ))}
                </ul>
            )}
        </DetailCard>
    );
};

export default KycReviewPanel;
